// Small shared pieces.

import type { ReactNode } from 'react';
import type React from 'react';
import { ApiError } from '../api.ts';
import { US_STATES, stateCode } from '../states.ts';
import { toIsoDate } from '../dates.ts';

export function Notice({ kind, children }: { kind: 'error' | 'ok' | 'info'; children: ReactNode }) {
  // role="alert" so a screen reader announces a validation failure rather than leaving the
  // applicant to wonder why the form did not submit.
  return (
    <div className={`notice ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof ApiError || error instanceof Error
    ? error.message
    : 'Something went wrong. Please try again.';
  return <Notice kind="error">{message}</Notice>;
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <p className="empty" role="status">{what}…</p>;
}

export function Field({
  label, hint, children,
}: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label>
      <span>{label}</span>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

/**
 * Formats an ISO date for reading, tolerating nulls and junk.
 *
 * Rendered in UTC deliberately. These are calendar dates -- a cycle opens on 1 October, an
 * application was submitted on 30 April -- stored as midnight UTC. Formatting them in the
 * reader's local zone shifts anyone west of Greenwich back a day, so a cycle opening on
 * 1 October reads as "September 30".
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
      });
}

// --- form controls ----------------------------------------------------------
//
// These exist so styling lives in one place. Writing `<input />` without a type attribute
// used to produce an unstyled, differently-sized box, because the stylesheet matched on
// `input[type=text]` and an attribute selector does not match an absent attribute. The
// stylesheet no longer depends on that, and these components mean a field cannot be declared
// without a type at all.

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export function TextInput({ type = 'text', ...props }: InputProps) {
  return <input type={type} {...props} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} />;
}

/** A checkbox with its label, which is laid out differently from every other control. */
export function Checkbox({
  label, ...props
}: InputProps & { label: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', gap: '0.6rem', alignItems: 'start' }}>
      <input type="checkbox" style={{ width: 'auto', marginTop: '0.25rem' }} {...props} />
      <span style={{ margin: 0 }}>{label}</span>
    </label>
  );
}

/** The award, as applicants should read it: "$20,000, paid over four years". */
export function formatAward(amount: number, years: number): string {
  const dollars = amount.toLocaleString('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  });
  const spelled = ['', 'one', 'two', 'three', 'four', 'five', 'six'][years] ?? String(years);
  return years > 1 ? `${dollars} scholarship, paid over ${spelled} years` : dollars;
}

/**
 * Props that make a whole table row open a record, while the row still contains a real
 * link for the keyboard and a screen reader.
 *
 * The row click is a convenience on top of that link, not a replacement for it: a <tr> with
 * an onClick is invisible to assistive technology and unreachable by tab. Clicks on anything
 * already interactive (the name link, the PDF link) are left alone, and a click that ends a
 * text selection does not navigate -- copying an email out of the table is a thing reviewers
 * do, and having it jump the page instead would be worse than not having row clicks at all.
 */
export function rowLinkProps(navigate: (to: string) => void, to: string) {
  return {
    className: 'row-link',
    onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (event.defaultPrevented) return;
      // Let modified clicks do what the browser would do with them.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if ((event.target as HTMLElement).closest('a, button, input, label, select, textarea')) return;
      if ((window.getSelection()?.toString() ?? '') !== '') return;
      navigate(to);
    },
  };
}

/**
 * A state dropdown.
 *
 * `value` may be a code, a full name, or something that is neither -- 67 migrated applicants
 * have the state "None". A value we cannot place is kept as its own option rather than being
 * dropped, because a <select> silently shows nothing for an unknown value, and the applicant
 * would then save a state they never chose without ever seeing it change.
 */
export function StateSelect({
  value, onChange, ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { value: string }) {
  const code = stateCode(value);
  const unrecognised = value.trim() !== '' && code === null;

  return (
    <Select value={code ?? value} onChange={onChange} {...props}>
      <option value="">Choose a state…</option>
      {unrecognised && <option value={value}>{value} (not a state — please correct)</option>}
      {US_STATES.map((s) => (
        <option key={s.code} value={s.code}>{s.name}</option>
      ))}
    </Select>
  );
}

/**
 * A date field: the browser's own calendar, and typing, and neither one lost.
 *
 * `<input type="date">` gives a picker and keyboard entry for free, but it only understands
 * `YYYY-MM-DD` and renders blank for anything else -- so pointing it at the legacy column
 * would quietly erase the 665 dates of birth on file that are written some other way. A value
 * that is already a date, however it was written, is converted and shown in the picker. One
 * that is not ("5", "01/07/`989") stays in a text box with a note, for the applicant to fix
 * rather than for us to guess at or throw away.
 */
export function DateInput({
  value, onChange, max, ...props
}: Omit<InputProps, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (value: string) => void;
  max?: string;
}) {
  const iso = toIsoDate(value);
  const unreadable = value.trim() !== '' && iso === null;

  if (unreadable) {
    return (
      <>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} {...props} />
        <p className="hint" style={{ color: 'var(--danger)' }}>
          We could not read “{value}” as a date.{' '}
          <button type="button" className="quiet" style={{ padding: 0 }} onClick={() => onChange('')}>
            Clear it and use the calendar
          </button>
        </p>
      </>
    );
  }

  return (
    <input
      type="date"
      value={iso ?? ''}
      // The control emits ISO already; anything else means the browser has no date support,
      // in which case the server's parser is what catches it.
      onChange={(e) => onChange(e.target.value)}
      min="1900-01-01"
      max={max}
      {...props}
    />
  );
}
